import { getSlotStatusCount } from "../../config/Services";
import { fetchSlotList, updateInstantDeliverySlot } from "../slotServices/slotApiService";

let slotState = {
  slotList: [],
  instantSlot: null,
  selectedSlotId: "ALL",
  isFetching: true,
};

let subscribers = [];

export const subscribeSlots = (callback) => {
  subscribers?.push(callback);

  callback(slotState);

  return () => {
    subscribers = subscribers?.filter((cb) => cb !== callback);
  };
};

const notifySubscribers = () => {
  subscribers?.forEach((cb) => cb(slotState));
};

export const waitForSlots = () => {
  return new Promise((resolve) => {
    if (!slotState?.isFetching) {
      resolve(getSlotIds());
    } else {
      const unsubscribe = subscribeSlots((state) => {
        if (!state.isFetching) {
          unsubscribe();
          resolve(getSlotIds());
        }
      });
    }
  });
};

const fetchSlotCounts = async (slotList) => {
  try {
    if (!slotList?.length) return;

    const slotIds = slotList?.map((slot) => slot?.slotId).filter((id) => id && id !== "ALL");

    if (!slotIds?.length) return;

    const res = await getSlotStatusCount({
      orgId: localStorage.getItem("orgId"),
      locationId: localStorage.getItem("locId"),
      slotId: slotIds,
      fulfilmentStatus: ["CREATED", "ACCEPTED", "PREPARING", "PACKAGED", "IN_TRANSIT", "DELIVERED"],
    });

    const countData = res?.data?.data || [];

    updateSlotCounts(countData);
  } catch (err) {}
};

export const refreshSlotCounts = () => fetchSlotCounts(slotState?.slotList);

export const fetchSlots = ({ date, orgId, locId }) => {
  slotState.isFetching = true;
  slotState.selectedSlotId = "ALL";
  notifySubscribers();

  fetchSlotList({ date, orgId, locId })
    .then((data) => {
      slotState.isFetching = false;
      // Preserve current selection — only reset to "ALL" on initial load (empty list)
      const currentSelectedId = slotState?.slotList?.length === 0 ? "ALL" : slotState?.selectedSlotId;

      const slots =
        data?.results?.map((cutoffItem) => ({
          slotId: cutoffItem?.slot?.slotId,
          startTime: cutoffItem?.slot?.startTime,
          endTime: cutoffItem?.slot?.endTime,
          createdCount: cutoffItem?.slot?.createdCount || 0,
          selected: cutoffItem?.slot?.slotId === currentSelectedId,
        })) || [];

      const allOrdersSlot = {
        slotId: "ALL",
        label: "All Orders",
        selected: currentSelectedId === "ALL",
        isAll: true,
      };

      slotState.selectedSlotId = currentSelectedId;

      // handle instant slot — always show in UI; use real data if available.
      // API returns instantDeliveryResults as a single object (older shape was an array).
      const instantRaw = data?.instantDeliveryResults;
      const instantFromApi = Array.isArray(instantRaw) ? instantRaw[0] : instantRaw;
      const instantSlotId = instantFromApi?.instantDeliveryId || "INSTANT";
      const instantSlot = {
        slotId: instantSlotId,
        startTime: instantFromApi?.instantDeliveryStartTime || null,
        endTime: instantFromApi?.instantDeliveryEndTime || null,
        active: instantFromApi?.active ?? true,
        instant: true,
        createdCount: instantFromApi?.createdCount || 0,
        selected: instantSlotId === currentSelectedId,
      };
      slotState.instantSlot = instantSlot;
      slots?.unshift(instantSlot);

      slotState.slotList = [allOrdersSlot, ...slots];

      notifySubscribers();
      fetchSlotCounts(slotState.slotList);
    })
    .catch(() => {
      slotState.isFetching = false;
      notifySubscribers();
    });
};

export const selectSlot = (slotId) => {
  slotState.selectedSlotId = slotId;

  slotState.slotList = slotState?.slotList?.map((slot) => ({
    ...slot,
    selected: slot?.slotId === slotId,
  }));

  notifySubscribers();
};

export const toggleInstantSlot = (reason) => {
  const payload = {
    instantDeliveryId: slotState?.instantSlot?.slotId,
    active: !slotState?.instantSlot?.active,
    reason,
  };

  // Returns a promise; only flips local state on a real success (es falsy).
  return updateInstantDeliverySlot(payload).then((data) => {
    if (data?.es) {
      return Promise.reject(new Error(data?.message || "Unable to update slot"));
    }
    slotState.instantSlot = {
      ...slotState?.instantSlot,
      active: !slotState?.instantSlot?.active,
    };

    notifySubscribers();
    return data;
  });
};

export const getSlotIds = () => {
  return slotState?.slotList
    ?.map((slot) => slot?.slotId)
    ?.filter((id) => id && id !== "ALL" && id !== "INSTANT");
};

export const updateSlotCounts = (countData = []) => {
  const countMap = countData.reduce((acc, item) => {
    acc[item.slotId] = item.createdCount;
    return acc;
  }, {});

  slotState.slotList = slotState?.slotList?.map((slot) => ({
    ...slot,
    createdCount: countMap[slot.slotId] || 0,
  }));

  notifySubscribers();
};

export const getSelectedSlotId = () => slotState?.selectedSlotId;
